"use client"

import { useIframeEvent } from "@/context/IframeContext"
import { updateLineItem } from "@/lib/data/cart"
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useTransition,
} from "react"

const SYNC_DEBOUNCE_MS = 320

export const UpdateCartItemButton = ({
  quantity,
  lineItemId,
  onOptimisticQuantity,
}: {
  quantity: number
  lineItemId: string
  /** Fires immediately on +/- so parents can update line totals without waiting for the server. */
  onOptimisticQuantity?: (quantity: number) => void
}) => {
  const [displayQty, setDisplayQty] = useState(quantity)
  const [isSyncing, setIsSyncing] = useState(false)
  const [direction, setDirection] = useState<"up" | "down">("up")
  const [bumpKey, setBumpKey] = useState(0)
  const [pressed, setPressed] = useState<"minus" | "plus" | null>(null)
  const [, startTransition] = useTransition()
  const iframeEvent = useIframeEvent()

  const displayQtyRef = useRef(displayQty)
  const committedQtyRef = useRef(quantity)
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const inFlightRef = useRef(false)
  const lineItemIdRef = useRef(lineItemId)
  const onOptimisticRef = useRef(onOptimisticQuantity)
  const iframeEventRef = useRef(iframeEvent)
  const pressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  displayQtyRef.current = displayQty
  lineItemIdRef.current = lineItemId
  onOptimisticRef.current = onOptimisticQuantity
  iframeEventRef.current = iframeEvent

  useEffect(() => {
    if (inFlightRef.current || debounceTimerRef.current) return
    setDisplayQty(quantity)
    committedQtyRef.current = quantity
  }, [quantity])

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current)
      if (pressTimerRef.current) clearTimeout(pressTimerRef.current)
    }
  }, [])

  const flush = useCallback(async () => {
    if (inFlightRef.current) return

    const target = displayQtyRef.current
    if (target === committedQtyRef.current) {
      setIsSyncing(false)
      return
    }

    inFlightRef.current = true
    setIsSyncing(true)
    const lineId = lineItemIdRef.current

    try {
      await updateLineItem({ lineId, quantity: target })
      committedQtyRef.current = target
      iframeEventRef.current.sentRefreshDataToIframe?.()
    } catch {
      const rollback = committedQtyRef.current
      setDisplayQty(rollback)
      displayQtyRef.current = rollback
      onOptimisticRef.current?.(rollback)
    } finally {
      inFlightRef.current = false
      if (displayQtyRef.current !== committedQtyRef.current) {
        void flush()
      } else {
        setIsSyncing(false)
      }
    }
  }, [])

  const scheduleFlush = useCallback(() => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current)
    setIsSyncing(true)
    debounceTimerRef.current = setTimeout(() => {
      debounceTimerRef.current = null
      startTransition(() => {
        void flush()
      })
    }, SYNC_DEBOUNCE_MS)
  }, [flush])

  const flashPress = (side: "minus" | "plus") => {
    setPressed(side)
    if (pressTimerRef.current) clearTimeout(pressTimerRef.current)
    pressTimerRef.current = setTimeout(() => setPressed(null), 220)
  }

  const changeBy = (delta: number) => {
    const next = displayQtyRef.current + delta
    if (next < 1) return

    const dir = delta > 0 ? "up" : "down"
    setDirection(dir)
    setBumpKey((k) => k + 1)
    flashPress(delta > 0 ? "plus" : "minus")
    setDisplayQty(next)
    displayQtyRef.current = next
    onOptimisticRef.current?.(next)
    scheduleFlush()
  }

  return (
    <div
      className={`bb-qty ${isSyncing ? "bb-qty--syncing" : ""}`}
      role="group"
      aria-label="Quantity"
    >
      <button
        type="button"
        className={`bb-qty-btn ${pressed === "minus" ? "bb-qty-btn--pressed" : ""}`}
        disabled={displayQty <= 1}
        aria-label="Decrease quantity"
        onClick={() => changeBy(-1)}
      >
        <span className="bb-qty-btn-icon" aria-hidden>
          −
        </span>
        <span className="bb-qty-ripple" aria-hidden />
      </button>

      <span className="bb-qty-value" aria-live="polite">
        <span
          key={bumpKey}
          className={`bb-qty-digit bb-qty-digit--${direction}`}
        >
          {displayQty}
        </span>
      </span>

      <button
        type="button"
        className={`bb-qty-btn bb-qty-btn--plus ${pressed === "plus" ? "bb-qty-btn--pressed" : ""}`}
        aria-label="Increase quantity"
        onClick={() => changeBy(1)}
      >
        <span className="bb-qty-btn-icon" aria-hidden>
          +
        </span>
        <span className="bb-qty-ripple" aria-hidden />
      </button>
    </div>
  )
}
